import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import Chip from '@mui/material/Chip';
import InputAdornment from '@mui/material/InputAdornment';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import type { TReviewReminderRecipient } from '@onesource/schemas';
import { getMediaUrl } from '@onesource/services';
import { DateTime } from 'luxon';
import { FC, useMemo, useState } from 'react';
import { MdSearch } from 'react-icons/md';

/** Long lists get a search box; short ones are faster to scan. */
const SEARCH_FROM = 8;

export const reminderKey = (r: TReviewReminderRecipient) => `${r.person.userUuid}:${r.form?.formId ?? ''}`;

interface Props {
  items: TReviewReminderRecipient[];
  selected: Set<string>;
  onChange: (selected: Set<string>) => void;
  /** The line's two texts: who or what, then the detail. */
  label: (r: TReviewReminderRecipient) => { primary: string; secondary: string };
  /** Show the person's avatar (off when every line is the same student). */
  showAvatar: boolean;
}

/**
 * Who or what a reminder goes to, each with a tick box. Everything not reminded in
 * the last 24 hours starts ticked; reminded ones show when and cannot be ticked.
 */
export const ReviewReminderPickList: FC<Props> = ({ items, selected, onChange, label, showAvatar }) => {
  const [query, setQuery] = useState('');
  const visible = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) {
      return items;
    }
    return items.filter(r => {
      const { primary, secondary } = label(r);
      return `${primary} ${secondary} ${r.person.email ?? ''}`.toLowerCase().includes(needle);
    });
  }, [items, query, label]);

  const pickable = visible.filter(r => !r.skipped);
  const pickedVisible = pickable.filter(r => selected.has(reminderKey(r))).length;
  const allOn = pickable.length > 0 && pickedVisible === pickable.length;

  const toggle = (r: TReviewReminderRecipient) => {
    const next = new Set(selected);
    const key = reminderKey(r);
    if (next.has(key)) {
      next.delete(key);
    } else {
      next.add(key);
    }
    onChange(next);
  };
  const toggleAll = () => {
    const next = new Set(selected);
    pickable.forEach(r => (allOn ? next.delete(reminderKey(r)) : next.add(reminderKey(r))));
    onChange(next);
  };

  return (
    <Box>
      {items.length >= SEARCH_FROM && (
        <TextField
          size="small"
          fullWidth
          placeholder="Search"
          value={query}
          onChange={e => setQuery(e.target.value)}
          inputProps={{ 'aria-label': 'Search the list' }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <MdSearch size={18} />
              </InputAdornment>
            ),
          }}
          sx={{ mb: 1 }}
        />
      )}
      <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', px: 0.5, borderBottom: '1px solid', borderColor: 'divider' }}>
          <Checkbox
            size="small"
            checked={allOn}
            indeterminate={pickedVisible > 0 && !allOn}
            disabled={pickable.length === 0}
            onChange={toggleAll}
            inputProps={{ 'aria-label': allOn ? 'Untick all' : 'Tick all' }}
          />
          <Typography variant="caption" sx={{ fontWeight: 700 }} role="status">
            {selected.size} of {items.filter(r => !r.skipped).length} selected
          </Typography>
        </Box>
        <Box role="list" sx={{ maxHeight: 280, overflowY: 'auto' }}>
          {visible.map(r => {
            const key = reminderKey(r);
            const { primary, secondary } = label(r);
            return (
              <Box
                role="listitem"
                key={key}
                component="label"
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  px: 0.5,
                  py: 0.5,
                  cursor: r.skipped ? 'default' : 'pointer',
                  opacity: r.skipped ? 0.6 : 1,
                  '& + &': { borderTop: '1px solid', borderColor: 'divider' },
                }}
              >
                <Checkbox
                  size="small"
                  checked={!r.skipped && selected.has(key)}
                  disabled={r.skipped}
                  onChange={() => toggle(r)}
                  inputProps={{ 'aria-label': `${primary}, ${secondary}` }}
                />
                {showAvatar && (
                  <Avatar
                    src={r.person.avatar ? getMediaUrl(r.person.avatar) : undefined}
                    sx={{ width: 28, height: 28 }}
                  >
                    {r.person.name.charAt(0).toUpperCase()}
                  </Avatar>
                )}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
                    {primary}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" noWrap display="block">
                    {secondary}
                  </Typography>
                </Box>
                {r.skipped && r.lastRemindedAt && (
                  <Chip
                    size="small"
                    label={`Reminded ${DateTime.fromJSDate(new Date(r.lastRemindedAt)).toRelative()}`}
                    sx={{ mr: 1 }}
                  />
                )}
              </Box>
            );
          })}
          {visible.length === 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ p: 2, textAlign: 'center' }}>
              No matches.
            </Typography>
          )}
        </Box>
      </Box>
    </Box>
  );
};
